import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import HttpBackend from 'i18next-http-backend'
import { loadDefaultJapaneseParser, loadDefaultSimplifiedChineseParser } from 'budoux'
import type { Parser } from 'budoux'

// Lazy-load BudouX parsers
let jaParser: Parser | null = null
let zhParser: Parser | null = null

function getJapaneseParser(): Parser {
  if (!jaParser) {
    jaParser = loadDefaultJapaneseParser()
  }
  return jaParser
}

function getChineseParser(): Parser {
  if (!zhParser) {
    zhParser = loadDefaultSimplifiedChineseParser()
  }
  return zhParser
}

// BudouX post-processor for i18next
// Uses zero-width space (U+200B) instead of <wbr> tags for safe text content
interface PostProcessorOptions {
  [key: string]: unknown
}

interface I18nextTranslator {
  language: string
  [key: string]: unknown
}

const budouXProcessor = {
  type: 'postProcessor' as const,
  name: 'budoux',
  process(value: string, _key: string, _options: PostProcessorOptions, translator: I18nextTranslator) {
    const lng = translator.language

    // Only apply to Japanese and Chinese
    if (lng !== 'ja' && lng !== 'zh') {
      return value
    }

    try {
      const parser = lng === 'ja' ? getJapaneseParser() : getChineseParser()
      const chunks = parser.parse(value)
      // Join with zero-width space (U+200B) to allow breaking without visible characters
      return chunks.join('\u200B')
    } catch (error) {
      console.warn('BudouX processing failed:', error)
      return value
    }
  },
}

// Initialize i18next
i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(budouXProcessor)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'ja', 'zh'],
    debug: true, // Enable debug to see interpolation issues
    ns: ['common'],
    defaultNS: 'common',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'deai-language',
    },
    interpolation: {
      escapeValue: false, // React already escapes values
      skipOnVariables: false, // Ensure interpolation happens
    },
    react: {
      useSuspense: true,
    },
    // Don't apply postProcess by default - only when explicitly requested
    // This ensures interpolation happens first
  })

export default i18n
